import React, {useState, useEffect} from 'react';
import Loading from '../Loading/index.js';
import {Helmet} from "react-helmet";
import BreadCrumbs from '../BreadCrumbs/index.js';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../uiComponents/MuiTable/StyledTable.js'
import APIUtils from '../../utils/APIUtils.js';
import SessionConstants from '../../config/SessionConstants.js';
import MuiTableHeader from '../../uiComponents/MuiTable/MuiTableHeader.js';
import TableFilterWorkflowRuns from '../../uiComponents/MuiTable/TableFilterWorkflowRuns.js';
import MuiTablePagination from '../../uiComponents/MuiTable/MuiTablePagination.js';
import {stableSort,getComparator} from '../../uiComponents/MuiTable/MuiHelper.js';
import { 
  TableBody,
  TableContainer,
  Button,
} from '@mui/material';
import { VisibilityOutlined } from '@mui/icons-material';
import FilterNameConfig from './FilterNameConfig.js';
import { isValid } from 'date-fns';
import { DateUtils } from '../../utils/DateUtils.js';
import makeStyles from '@mui/styles/makeStyles';
import './index.css';
import PipelineStepsModal from './PipelineStepsModal/index.js';
import WorkflowRunStatusIcon from '../../icons/WorkflowRunStatusIcon.js';

const useStyles = makeStyles({
  // Compact modal-trigger action; hugs its content.
  workflowStepsButtons: {
    padding: '5px 10px',
    lineHeight: 1.2,
    // Global 508 focus style sets border:none; keep a matching 1px border so the box doesn't shift.
    '&:focus-visible': {
      border: '1px solid var(--accent-primary-color)',
    },
  },
});

const headCells = [
  { label: 'Run Name', id: 'name', numeric: false, disablePadding: false },
  { label: 'Workflow Type', id: 'type', numeric: false, disablePadding: false },
  { label: 'Start', id: 'start', numeric: false, disablePadding: false },
  { label: 'Finish', id: 'finish', numeric: false, disablePadding: false },
  { label: 'Status', id: 'status', numeric: false, disablePadding: false },
  { label: 'Pipeline Steps', id: 'pipelineSteps', numeric: false, disablePadding: false, type: 'noSort' }, 
];

export default function WorkflowRuns(props) {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([])
  const [loading, setLoading] = useState(true);
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [order, setOrder] = useState('desc'); //default sort direction
  const [orderBy, setOrderBy] = useState('start'); //default sort column for table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchFilter, setSearchFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState([]);
  const [filterBy, setFilterBy] = useState([]);
  const [tableDataKeyList, setTableDataKeyList] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState("All Time");
  const [pipelineStepsModalIsOpen, setPipelineStepsModalIsOpen] = useState(false);
  const [pipelineStepsData, setPipelineStepsData] = useState([]);
  const [pipelineStepsLoading, setPipelineStepsLoading] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null); 

  useEffect(() => { 
    setBreadCrumbs([{
      name: 'WorkflowRuns',
    }]);
  }, [] );

  // intialize tableData
  useEffect(() => { 
    getWorkflowRuns();
  }, [] );

  useEffect(() => {
    applyAllFilters();
  }, [selectedTimeRange, customStartDate, customEndDate, searchFilter, filterBy, initialRows]);

  const getWorkflowRuns = async () => {
    try {
      setLoading(true);
      var programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
      const response = await APIUtils.getWorkflowRunsByProgramId(programId);
      // Capture all keys in the table data to use for filtering
      // console.log("Workflow Runs Response:", JSON.stringify(response));
      const workflowRuns = response.result || [];
      const allTableDataKeys = workflowRuns.reduce((keys, obj) => {
        Object.keys(obj).forEach(key => {
          if (!keys.includes(key)) {
            keys.push(key)
          }
        });
        return keys;
      }, []);

      setInitialRows(workflowRuns);
      setRows(workflowRuns);
      setTableData(workflowRuns);
      // Set table keys in key list to use for filtering
      setTableDataKeyList(allTableDataKeys);
      // console.log("table data:" , JSON.stringify(workflowRuns));
    }
    catch (error) {
      console.error("Error Getting Workflows: " + error);
      setLoading(false);
    }
    finally {
      setLoading(false);
    }
  }

  const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
  };
  
  const handleChangePage = (event, newPage) => {
      setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
  };

  const minDate = new Date(2020, 0, 1);
  const maxDate = new Date();

 const applyAllFilters = (
    rowsToFilter = initialRows,
    {
      selectedTimeRange: _selectedTimeRange = selectedTimeRange,
      customStartDate: _customStartDate = customStartDate,
      customEndDate: _customEndDate = customEndDate,
      searchFilter: _searchFilter = searchFilter,
      filterBy: _filterBy = filterBy,
    } = {}
  ) => {
    let startDate, endDate;
    const currentDate = new Date();

    if (_selectedTimeRange === "Custom Range") {
      const startValid = _customStartDate && isValid(new Date(_customStartDate));
      const endValid = _customEndDate && isValid(new Date(_customEndDate));

      // Use minDate if start is empty or invalid, maxDate if end is empty or invalid
      startDate = startValid ? new Date(_customStartDate) : minDate;
      endDate = endValid ? new Date(_customEndDate) : maxDate;

      // If both are valid but range is invalid (start > end), reset only the start to minDate
      if (startValid && endValid && startDate > endDate) {
        startDate = minDate;
      }
    } else if (_selectedTimeRange === "All Time") {
      startDate = null;
      endDate = null;
    } else {
      startDate = new Date();
      endDate = currentDate;
      switch (_selectedTimeRange) {
        case "1 Week":
          startDate.setDate(currentDate.getDate() - 7);
          break;
        case "1 Month":
          startDate.setMonth(currentDate.getMonth() - 1);
          break;
        case "6 Months":
          startDate.setMonth(currentDate.getMonth() - 6);
          break;
        case "1 Year":
          startDate.setFullYear(currentDate.getFullYear() - 1);
          break;
        case "2 Years":
          startDate.setFullYear(currentDate.getFullYear() - 2);
          break;
        case "5 Years":
          startDate.setFullYear(currentDate.getFullYear() - 5);
          break;
        default:
          break;
      }
    }

    let filtered = rowsToFilter;

    // Date filtering
    if (startDate && endDate) {
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter(obj => {
        const objDate = new Date(obj.start);
        return objDate >= startDate && objDate <= endDate;
      });
    }

    // Value filtering (for non-date columns)
    if (
      _searchFilter &&
      !["start", "finish"].includes(_searchFilter) &&
      _filterBy
    ) {
      const filterValues = Array.isArray(_filterBy) ? _filterBy : [_filterBy];
      if (filterValues.length > 0) {
        filtered = filtered.filter(obj => filterValues.includes(obj[_searchFilter]));
      }
    }

    setTableData(filtered);
    setRows(filtered);
    setPage(0);
  };

  const handleDaysRangeChange = (event) => {
    const selectedTimeRange = event.target.value;
    setSelectedTimeRange(selectedTimeRange);

    // If changing Date Range filter from "Custom Range", clear the custom dates
    if (selectedTimeRange !== "Custom Range") {
      setCustomStartDate('');
      setCustomEndDate('');
    }

    applyAllFilters(initialRows, { selectedTimeRange, customStartDate: '', customEndDate: '' });
  };

  //++++++++++++++FILTERS+++++++++++++++++++++++++
  // Search Filter Type Select
  const filterTypeChange = (event) => {
    const selectedKey = event.target.value;
    setSearchFilter(selectedKey);
    setFilterBy([]);
    setFilterOptions(selectedKey ? [...new Set(initialRows.map(obj => obj[selectedKey]))] : []);
    applyAllFilters(undefined, { searchFilter: selectedKey, filterBy: [] });
  };

  const handleFilterChange = (event) => {
    const selectedValue = event.target.value;
    setFilterBy(selectedValue);
    applyAllFilters(undefined, { filterBy: selectedValue });
  };

  const clearFilters = (event) => {
    setSearchFilter('');
    setFilterBy([]);
    setCustomStartDate('');
    setCustomEndDate('');
    setSelectedTimeRange('All Time');
    setTableData(initialRows);
    setRows(initialRows);
    // reset filterOptions to empty or to all possible values for the default column
    setFilterOptions([]);
  };

  const openPipelineStepsModal = async (row) => {
    try {
      // Show loading spinner on the page while fetching data
      setPipelineStepsLoading(true);
      const response = await APIUtils.getWorkflowStepsByWorkflowRunId(row.workflowRunId);
      // Add the workflow run name from the row data to the response
      setPipelineStepsData({ ...response, name: row.name });
      // Open modal only after data is loaded
      setPipelineStepsModalIsOpen(true);
    } catch (error) {
      console.error("Error Getting Workflow Steps: " + error);
    } finally {
      setPipelineStepsLoading(false);
    }
  };

  const closePipelineStepsModal = () => {
    setPipelineStepsModalIsOpen(false);
  };

  const pipelineStepsCell = (row) => {
    return (
      <Button 
        className={classes.workflowStepsButtons}
        variant="outlined"
        color="primary"
        aria-label="View pipeline steps" 
        onClick={() => openPipelineStepsModal(row)}
      >
        <VisibilityOutlined fontSize="small" className="iconMarginRight" />View
      </Button>
    )
  }

  return (
    <div className="pageMinHeight">
      <Loading isLoading={loading || pipelineStepsLoading}/>
      <Helmet>
        <title>ED3N - Workflow Runs</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Workflow Runs</h1>
      {!(loading) ? (
        <>
          <div className="workflowRunsTableFilterGrid">
            <TableFilterWorkflowRuns 
              rows={rows} 
              tableDataKeyList={tableDataKeyList}
              filterNameConfig={FilterNameConfig}
              searchFilter={searchFilter}
              filterBy={filterBy}
              filterOptions={filterOptions}
              onClearFilters={clearFilters}
              handleFilterChange={handleFilterChange}
              onFilterTypeChange={filterTypeChange}
              daysRangeDefined={true}
              handleDaysRangeChange={handleDaysRangeChange}
              selectedTimeRange={selectedTimeRange}
              customStartDate={customStartDate}
              setCustomStartDate={setCustomStartDate}
              customEndDate={customEndDate}
              setCustomEndDate={setCustomEndDate}
            />
          </div>
          <div>
            <StyledPaper>
              <TableContainer>
                <StyledTable
                  size='small'
                  aria-label="Workflow Runs" 
                >
                  <MuiTableHeader 
                    headCells={headCells} 
                    order={order}  
                    orderBy={orderBy} 
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                  { tableData.length ? (
                    stableSort(tableData, getComparator(order, orderBy))
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row, index) => {
                      return (
                        <StyledTableRow tabIndex={-1} key={row.workflowRunId}>
                          <StyledTableCell>{row.name}</StyledTableCell>
                          <StyledTableCell>{row.type}</StyledTableCell>
                          <StyledTableCell>{DateUtils.FormatLocalDate(row.start)}</StyledTableCell>
                          <StyledTableCell>{DateUtils.FormatLocalDate(row.finish)}</StyledTableCell>
                          <StyledTableCell style={{width: '17%'}}><WorkflowRunStatusIcon status={row.status}><span>{row.status}</span></WorkflowRunStatusIcon></StyledTableCell> 
                          <StyledTableCell style={{width: '17%'}}>{pipelineStepsCell(row)}</StyledTableCell>
                        </StyledTableRow>
                      );
                      })
                    ) : (
                      <StyledTableRow>
                        <StyledTableCell style={{textAlign: 'center'}} colSpan={12}>No workflow records found</StyledTableCell>
                      </StyledTableRow>
                    )
                  }
                  </TableBody>
                </StyledTable>
              </TableContainer>
              <MuiTablePagination 
                rowsPerPage={rowsPerPage}
                page={page}
                count={tableData.length}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </StyledPaper>
          </div>
          <PipelineStepsModal
            closeModal={closePipelineStepsModal}
            modalIsOpen={pipelineStepsModalIsOpen}
            pipelineSteps={pipelineStepsData}
          />
        </>
      ) : (
        // spacing for load spinner
        (<div className="noData"></div>)
      )}
    </div>
  );
}
